import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ZoomIn, ZoomOut, Grid, Ruler, Eye, MoreVertical } from 'lucide-react';
import { useFigureEditorUiState } from '@/hooks/useFigureEditorUiState';

interface CanvasToolbarProps {
  onNavigateToGallery?: () => void;
  onNavigateToFigures?: () => void;
  canvasWidthMm?: number; // Canvas width in mm for fit-to-width calculation
  containerWidthPx?: number; // Container width in pixels
}

export function CanvasToolbar({ canvasWidthMm, containerWidthPx }: CanvasToolbarProps) {
  const {
    canvasZoom,
    gridVisible,
    rulersVisible,
    showPanelLabels,
    showPanelBorders,
    previewAtJournalSize,
    setZoom,
    toggleGrid,
    toggleRulers,
    updateState,
  } = useFigureEditorUiState();

  const zoomOptions = [
    { value: 0.5, label: '50%' },
    { value: 0.75, label: '75%' },
    { value: 1, label: '100%' },
    { value: 1.25, label: '125%' },
    { value: 1.5, label: '150%' },
    { value: 'fit', label: 'Fit to Width' },
  ];

  const handleZoomChange = (value: string) => {
    console.log('🔍 [CanvasToolbar] handleZoomChange called with value:', value);
    if (value === 'fit') {
      // Fit to width: calculate zoom to fit canvas width to container
      if (canvasWidthMm && containerWidthPx) {
        const canvasWidthPx = (canvasWidthMm / 25.4) * 96; // Convert mm to pixels at 96 DPI
        const fitZoom = containerWidthPx / canvasWidthPx;
        console.log('🔍 [CanvasToolbar] Fit to width calculation:', { canvasWidthMm, containerWidthPx, canvasWidthPx, fitZoom });
        setZoom(Math.min(2, Math.max(0.25, fitZoom)));
      } else {
        // Fallback: use reasonable defaults
        const estimatedContainerWidth = containerWidthPx ?? 1200;
        const estimatedCanvasWidth = (canvasWidthMm ?? 180) * (96 / 25.4);
        const fitZoom = estimatedContainerWidth / estimatedCanvasWidth;
        console.log('🔍 [CanvasToolbar] Fit to width (fallback):', { estimatedContainerWidth, estimatedCanvasWidth, fitZoom });
        setZoom(Math.min(2, Math.max(0.25, fitZoom)));
      }
    } else {
      const zoomValue = parseFloat(value);
      console.log('🔍 [CanvasToolbar] Setting zoom from dropdown:', zoomValue);
      setZoom(zoomValue);
    }
  };

  return (
    <div className="bg-card border-b border-border h-12 px-4 flex items-center justify-between">
      {/* Left side */}
      <div className="flex items-center gap-2">
        {/* Zoom controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newZoom = Math.max(0.25, canvasZoom - 0.25);
              console.log('🔍 [CanvasToolbar] Zoom Out clicked:', { currentZoom: canvasZoom, newZoom });
              setZoom(newZoom);
            }}
            disabled={canvasZoom <= 0.25}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Select value={String(canvasZoom)} onValueChange={handleZoomChange}>
            <SelectTrigger className="w-24 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {zoomOptions.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newZoom = Math.min(2, canvasZoom + 0.25);
              console.log('🔍 [CanvasToolbar] Zoom In clicked:', { currentZoom: canvasZoom, newZoom });
              setZoom(newZoom);
            }}
            disabled={canvasZoom >= 2}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        <div className="h-6 w-px bg-border mx-2" />

        {/* Grid toggle */}
        <Button
          variant={gridVisible ? 'default' : 'ghost'}
          size="sm"
          onClick={toggleGrid}
          title="Grid"
        >
          <Grid className="h-4 w-4" />
        </Button>

        {/* Rulers toggle */}
        <Button
          variant={rulersVisible ? 'default' : 'ghost'}
          size="sm"
          onClick={toggleRulers}
          title="Ruler"
        >
          <Ruler className="h-4 w-4" />
        </Button>

        {/* View options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" title="View options">
              <Eye className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuCheckboxItem
              checked={showPanelLabels}
              onCheckedChange={(checked) => updateState({ showPanelLabels: checked })}
            >
              Show panel labels
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={showPanelBorders}
              onCheckedChange={(checked) => updateState({ showPanelBorders: checked })}
            >
              Show panel borders
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={previewAtJournalSize}
              onCheckedChange={(checked) => updateState({ previewAtJournalSize: checked })}
            >
              Preview at journal size
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Right side - could add save status, export button here */}
    </div>
  );
}

